import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Avatar,
  Divider,
  IconButton,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  useDisclosure,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import email_icon from '../Assets/icons/email_icon.svg';
import edit_pin_icon from '../Assets/icons/edit_pin_icon.svg';
import account_icon from '../Assets/icons/account_icon.svg';
import logout_icon from '../Assets/icons/logout_icon.svg';
// import lock_icon from '../Assets/icons/lock-solid.svg';
import lock_icon from '../Assets/icons/lock.svg';
import { useNavigate } from 'react-router-dom';
import { useDeleteUserAccount, useResetPasswordDirect } from '../utils/auth.api';
import { toast } from 'react-toastify';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { InputGroup, InputRightElement } from '@chakra-ui/react';
import { useAuthStore } from '../store/authStore';

const Settings = ({ onBack }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isPasswordOpen, onOpen: onPasswordOpen, onClose: onPasswordClose } = useDisclosure();
  const deleteUserAccountMutation = useDeleteUserAccount();
  const resetPasswordMutation = useResetPasswordDirect();
  const authLogout = useAuthStore((state) => state.logout);

  useEffect(() => {
    // Get username and email from localStorage
    const storedUsername = localStorage.getItem('username') || 'Annie John';
    const storedEmail = localStorage.getItem('email') || 'annie.john@example.com';
    
    setUsername(storedUsername);
    setEmail(storedEmail);
    setNewUsername(storedUsername);
  }, []);

  const handleEditName = () => {
    setNewUsername(username);
    onOpen();
  };

  const handleSaveName = () => {
    if (newUsername.trim()) {
      setUsername(newUsername.trim());
      localStorage.setItem('username', newUsername.trim());
      onClose();
    }
  };

  const handleLogout = () => {
    authLogout({ reload: false });
    navigate('/dashboard');
  };

  const handleDeleteAccount = () => {
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    const userId = localStorage.getItem('userid');
    if (!userId) {
      alert('User ID not found');
      return;
    }

    try {
      const response = await deleteUserAccountMutation.mutateAsync(userId);
      // Show success toast
      toast.success('Account deleted successfully', {
        autoClose: 3000,
        position: 'top-right',
      });
      
      // Clear localStorage
      localStorage.clear();
      
      // Small delay to show toast before redirect
      setTimeout(() => {
        // Redirect to autofiyai.com with accountDeleted parameter
        window.location.href = 'https://autofiyai.com/?accountDeleted=true';
      }, 500);
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(error.message || 'An error occurred while deleting your account. Please try again.');
    }
  };

  const handleChangePassword = () => {
    setNewPassword('');
    setConfirmPassword('');
    onPasswordOpen();
  };

  const handleSavePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in both password fields', {
        autoClose: 3000,
        position: 'top-right',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters', {
        autoClose: 3000,
        position: 'top-right',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match', {
        autoClose: 3000,
        position: 'top-right',
      });
      return;
    }

    const userId = localStorage.getItem('userid');
    if (!userId) {
      toast.error('User ID not found', {
        autoClose: 3000,
        position: 'top-right',
      });
      return;
    }

    try {
      const payload = {
        user_id: userId,
        new_password: newPassword,
      };

      await resetPasswordMutation.mutateAsync(payload);
      
      toast.success('Password changed successfully', {
        autoClose: 3000,
        position: 'top-right',
      });
      
      setNewPassword('');
      setConfirmPassword('');
      onPasswordClose();
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'An error occurred while changing your password. Please try again.', {
        autoClose: 3000,
        position: 'top-right',
      });
    }
  };

  return (
    <Box bg="white" minH="100vh">
      {/* Back Header */}
      <Flex
        as="header"
        align="center"
        px={4}
        py={4}
        borderBottom="1px"
        borderBottomColor="gray.200"
      >
    <IconButton
  icon={<ChevronLeftIcon boxSize={8} />}
  variant="ghost"
  aria-label="Back"
  color="black"
  _hover={{ bg: 'gray.100' }}
  onClick={() => {
    if (onBack) onBack();     // call parent callback if provided
    navigate('/dashboard');   // then go to dashboard
  }}
/>
        <HStack spacing={2} ml={2}>
          <Text fontSize="lg" fontWeight="bold">
            Settings
          </Text>
        </HStack>
      </Flex>

      {/* Profile Section */}
      <Box p={4}>
        <HStack spacing={4} align="center">
          <Avatar
            size="lg"
            name={username}
            src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150"
          />
          <Text fontSize="lg" fontWeight="bold">
            {username}
          </Text>
        </HStack>
      </Box>

      {/* Account Section */}
      <Box px={4}>
        <Text fontSize="lg" fontWeight="bold" mb={3} ml={3} color="gray.700">
          Account
        </Text>
        <VStack spacing={0} align="stretch">
          {/* Edit Name Button */}
          <Button
            variant="ghost"
            py={4}
            px={3}
            borderRadius={0}
            _hover={{ bg: 'gray.50' }}
            justifyContent="flex-start"
            w="full"
            onClick={handleEditName}
          >
            <Flex justify="space-between" align="center" w="full">
              <Flex align="center" gap={6}>
                <Image src={edit_pin_icon} alt="Edit" w="5" h="5" />
                <Text 
                  fontSize="14px" 
                  fontWeight="400" 
                  lineHeight="19.5px" 
                  letterSpacing="0%"
                  fontFamily="Arial"
                >
                  Edit Name
                </Text>
              </Flex>
              <ChevronRightIcon />
            </Flex>
          </Button>

          {/* Edit Email Button */}
          <Button
            variant="ghost"
            py={4}
            px={3}
            borderRadius={0}
            _hover={{ bg: 'gray.50' }}
            justifyContent="flex-start"
            w="full"
          >
            <Flex justify="space-between" align="center" w="full">
              <Flex align="center" gap={6}>
                <Image src={email_icon} alt="Email" w="5" h="5" />
                <Text 
                  fontSize="14px" 
                  fontWeight="400" 
                  lineHeight="19.5px" 
                  letterSpacing="0%"
                  fontFamily="Arial"
                >
                  {email}
                </Text>
              </Flex>
              <ChevronRightIcon />
            </Flex>
          </Button>

          {/* Change Password Button */}
          <Button
            variant="ghost"
            py={4}
            px={3}
            borderRadius={0}
            _hover={{ bg: 'gray.50' }}
            justifyContent="flex-start"
            w="full"
            onClick={handleChangePassword}
          >
            <Flex justify="space-between" align="center" w="full">
              <Flex align="center" gap={6}>
                <Image src={lock_icon} alt="Change Password" w="5" h="5" />
                <Text 
                  fontSize="14px" 
                  fontWeight="400" 
                  lineHeight="19.5px" 
                  letterSpacing="0%"
                  fontFamily="Arial"
                >
                  Change Password
                </Text>
              </Flex>
              <ChevronRightIcon />
            </Flex>
          </Button>

          <Button
            variant="ghost"
            py={4}
            px={3}
            borderRadius={0}
            _hover={{ bg: 'gray.50' }}
            justifyContent="flex-start"
            w="full"
            onClick={handleDeleteAccount}
          >
            <Flex justify="space-between" align="center" w="full">
              <Flex align="center" gap={6}>
                <Image src={account_icon} alt="Email" w="6" h="6" />
                <Text 
                  fontSize="14px" 
                  fontWeight="400" 
                  lineHeight="19.5px" 
                  letterSpacing="0%"
                  fontFamily="Arial"
                >
                  Delete Account
                </Text>
              </Flex>
              <ChevronRightIcon />
            </Flex>
          </Button>
        </VStack>
      </Box>

      <Divider my={4} />

      {/* Sign Out Section */}
      <Box px={4}>
        <Button
          variant="ghost"
          py={4}
          px={3}
          w="full"
          borderRadius={0}
          _hover={{ bg: 'gray.50'}}
          justifyContent="flex-start"
        >
          <Flex justify="space-between" align="center" w="full"    onClick={handleLogout}>
            <Flex align="center" gap={6}>
              <Image src={logout_icon} alt="Logout" w="5" h="5" />
              <Text  fontSize="14px" 
                  fontWeight="400" 
                  onClick={handleLogout}
                  lineHeight="19.5px" 
                  letterSpacing="0%"
                  fontFamily="Arial">Sign Out</Text>
            </Flex>
            <ChevronRightIcon  />
          </Flex>
        </Button>
      </Box>

      {/* Edit Name Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Name</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Enter new name"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              size="lg"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveName}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Account Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Account</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete your account? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onDeleteClose}
              isDisabled={deleteUserAccountMutation.isLoading}
            >
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleDeleteConfirm}
              isLoading={deleteUserAccountMutation.isLoading}
              loadingText="Deleting..."
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change Password Modal */}
      <Modal isOpen={isPasswordOpen} onClose={onPasswordClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>New Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    size="lg"
                  />
                  <InputRightElement width="4.5rem">
                    <IconButton
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      icon={showNewPassword ?  <ViewIcon />:<ViewOffIcon />}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel>Confirm Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    size="lg"
                  />
                  <InputRightElement width="4.5rem">
                    <IconButton
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      icon={showConfirmPassword ?  <ViewIcon />:<ViewOffIcon />}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onPasswordClose}
              isDisabled={resetPasswordMutation.isLoading}
            >
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSavePassword}
              isLoading={resetPasswordMutation.isLoading}
              loadingText="Changing..."
            >
              Change Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Settings;